import { useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Send, Phone, Mail, Instagram, MapPin } from "lucide-react";
import { BrandMark } from "@/components/site/BrandMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Full Home Makeover",
  "Hacking & Masonry",
  "Flooring & Tiling",
  "Wallpaper Installation",
  "Interior Design Consultation",
  "Carpentry & Joinery",
  "Other",
];

const PROPERTY_TYPES = ["BTO", "Resale HDB", "Condo", "Landed", "Other"];
const BUDGETS = [
  "Below S$30,000",
  "S$30,000 – S$50,000",
  "S$50,000 – S$80,000",
  "S$80,000 – S$120,000",
  "Above S$120,000",
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    property_type: "",
    service: "",
    budget: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please share your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — we'll be in touch within 24 hours.");

      // Build WhatsApp message
      const lines = [
        `Hello Noah+Rayn, I'd love a quote.`,
        `Name: ${form.name}`,
        `Email: ${form.email}`,
        `Phone: ${form.phone}`,
        form.property_type && `Property: ${form.property_type}`,
        form.service && `Service: ${form.service}`,
        form.budget && `Budget: ${form.budget}`,
        form.message && `Notes: ${form.message}`,
      ].filter(Boolean);
      const wa = `https://wa.me/6589867250?text=${encodeURIComponent(lines.join("\n"))}`;
      window.open(wa, "_blank", "noopener,noreferrer");

      setForm({
        name: "",
        email: "",
        phone: "",
        property_type: "",
        service: "",
        budget: "",
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please WhatsApp us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[color:var(--nr-bg)] py-24 md:py-32"
    >
      <Toaster
        position="top-center"
        richColors
        toastOptions={{
          style: { fontFamily: "Manrope" },
        }}
      />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left intro + contact info */}
        <div className="lg:col-span-5 reveal">
          <p className="overline mb-5">Start the conversation</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-[color:var(--nr-ink)]">
            Tell us about{" "}
            <em className="italic font-light text-[color:var(--nr-accent)]">
              your home.
            </em>
          </h2>
          <p className="mt-6 text-[color:var(--nr-ink-soft)] text-base lg:text-lg leading-relaxed max-w-md">
            Share a few details and we'll come back within 24 hours with a
            no-obligation quote — and an honest opinion on what your space
            actually needs.
          </p>

          <div className="mt-12 space-y-6">
            <a
              href="https://wa.me/6589867250"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-1"
              className="flex items-center gap-4 group"
            >
              <div className="w-12 h-12 rounded-full bg-[color:var(--nr-bg-soft)] flex items-center justify-center ring-1 ring-[color:var(--nr-line)] group-hover:bg-[color:var(--nr-accent)] transition-colors">
                <Phone size={18} className="text-[color:var(--nr-accent)] group-hover:text-white transition-colors" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)]">WhatsApp / Call</div>
                <div className="font-serif-display text-xl text-[color:var(--nr-ink)]">+65 8986 7250</div>
                <div className="font-serif-display text-xl text-[color:var(--nr-ink)]">+65 8898 2967</div>
              </div>
            </a>

            <a
              href="mailto:contactus@noahandrayn.com.sg"
              data-testid="contact-email"
              className="flex items-center gap-4 group"
            >
              <div className="w-12 h-12 rounded-full bg-[color:var(--nr-bg-soft)] flex items-center justify-center ring-1 ring-[color:var(--nr-line)] group-hover:bg-[color:var(--nr-accent)] transition-colors">
                <Mail size={18} className="text-[color:var(--nr-accent)] group-hover:text-white transition-colors" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)]">Email</div>
                <div className="font-serif-display text-xl text-[color:var(--nr-ink)]">contactus@noahandrayn.com.sg</div>
              </div>
            </a>

            <a
              href="https://instagram.com/noahandrayn"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-instagram"
              className="flex items-center gap-4 group"
            >
              <div className="w-12 h-12 rounded-full bg-[color:var(--nr-bg-soft)] flex items-center justify-center ring-1 ring-[color:var(--nr-line)] group-hover:bg-[color:var(--nr-accent)] transition-colors">
                <Instagram size={18} className="text-[color:var(--nr-accent)] group-hover:text-white transition-colors" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)]">Instagram</div>
                <div className="font-serif-display text-xl text-[color:var(--nr-ink)]">@Noahandrayn</div>
              </div>
            </a>

            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[color:var(--nr-bg-soft)] flex items-center justify-center ring-1 ring-[color:var(--nr-line)]">
                <MapPin size={18} className="text-[color:var(--nr-accent)]" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)]">Service Area</div>
                <div className="font-serif-display text-xl text-[color:var(--nr-ink)]">Islandwide · Singapore</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right form */}
        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="lg:col-span-7 bg-[color:var(--nr-bg-soft)] rounded-2xl p-8 lg:p-12 border border-[color:var(--nr-line)] reveal"
        >
          <div className="flex flex-col items-start gap-2 mb-7 pb-5 border-b border-[color:var(--nr-line)]">
            <BrandMark className="w-10 h-10 text-[color:var(--nr-accent)]" data-testid="contact-brandmark" />
            <div className="leading-tight">
              <div className="font-serif-display text-xl text-[color:var(--nr-ink)]">
                noah<span className="text-[color:var(--nr-accent)]">+</span>rayn
              </div>
              <div className="text-[0.6rem] uppercase tracking-[0.22em] text-[color:var(--nr-ink-soft)] mt-1">
                Pte Ltd  ·  Building Meaningful Spaces
              </div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)]">Full Name *</label>
              <input
                data-testid="contact-input-name"
                className="nr-input"
                placeholder="Jane Tan"
                value={form.name}
                onChange={update("name")}
                required
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)]">Email *</label>
              <input
                data-testid="contact-input-email"
                className="nr-input"
                type="email"
                placeholder="jane@example.com"
                value={form.email}
                onChange={update("email")}
                required
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)]">Phone *</label>
              <input
                data-testid="contact-input-phone"
                className="nr-input"
                placeholder="+65 9123 4567"
                value={form.phone}
                onChange={update("phone")}
                required
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)]">Property Type</label>
              <select
                data-testid="contact-select-property"
                className="nr-input"
                value={form.property_type}
                onChange={update("property_type")}
              >
                <option value="">Select…</option>
                {PROPERTY_TYPES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)]">Service</label>
              <select
                data-testid="contact-select-service"
                className="nr-input"
                value={form.service}
                onChange={update("service")}
              >
                <option value="">Select…</option>
                {SERVICES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)]">Budget</label>
              <select
                data-testid="contact-select-budget"
                className="nr-input"
                value={form.budget}
                onChange={update("budget")}
              >
                <option value="">Select…</option>
                {BUDGETS.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-6">
            <label className="text-xs uppercase tracking-[0.2em] text-[color:var(--nr-ink-soft)]">Tell us about your home</label>
            <textarea
              data-testid="contact-input-message"
              className="nr-textarea mt-2"
              placeholder="Block, unit type, target move-in date, any inspiration you'd like to share…"
              value={form.message}
              onChange={update("message")}
            />
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-xs text-[color:var(--nr-ink-soft)] max-w-sm">
              By sending, you agree to be contacted by Noah+Rayn Pte Ltd via
              phone, email or WhatsApp.
            </p>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="btn-primary rounded-full px-8 py-4 text-sm font-semibold tracking-wide inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? "Sending…" : "Send Enquiry"}
              <Send size={16} />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};
